import { LoadingButton } from "@mui/lab";
import { Box, FormHelperText, Grid, Typography } from "@mui/material";
import { CheckoutContext } from "contexts/Checkout";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { AddOnItemName } from "schemas/dashboard";

import { useCheckout } from "components/StripeCheckout/useCheckout";

import styles from "./styles";

const BundleIcons = {
  [AddOnItemName.email_volume_monthly]: "✉️",
  [AddOnItemName.email_volume_yearly]: "✉️",
  [AddOnItemName.email_volume_pro_monthly]: "✉️",
  [AddOnItemName.email_volume_pro_yearly]: "✉️",
  [AddOnItemName.support_call]: "💎",
};
const UnitName = {
  [AddOnItemName.support_call]: "call",
  [AddOnItemName.email_volume_monthly]: "package",
  [AddOnItemName.email_volume_yearly]: "package",
  [AddOnItemName.email_volume_pro_monthly]: "package",
  [AddOnItemName.email_volume_pro_yearly]: "package",
};
interface Props {
  addOnItemName: AddOnItemName;
}

const EmailAddOn = ({ addOnItemName }: Props) => {
  const { applyAddOnLoading, addOnItems } = useContext(CheckoutContext);
  const { addOns } = useContext(SubscriptionContext);
  const { applyAddOnItem, canPurchaseMoreEmailVolume, isAtMaxEmailVolume } =
    useCheckout();
  const unit = UnitName[addOnItemName as keyof typeof UnitName];
  const quantity =
    addOnItems?.find((x) => x.name === addOnItemName)?.quantity || 0;
  const [error, setError] = useState<string>("");

  const emailVolumeNames = [
    AddOnItemName.email_volume_monthly,
    AddOnItemName.email_volume_yearly,
    AddOnItemName.email_volume_pro_monthly,
    AddOnItemName.email_volume_pro_yearly,
  ];
  const isEmailVolume = emailVolumeNames.includes(addOnItemName);

  const handleAddToOrder = () => {
    if (isEmailVolume) {
      if (!validateEmailVolumes()) return;
    } else {
      const maxQuantity = addOns[addOnItemName]?.max_quantity;
      if (quantity >= maxQuantity) {
        setError(
          `This item is limit to ${addOns[addOnItemName]?.max_quantity} per order.`,
        );
        return;
      }
    }

    setError("");
    applyAddOnItem({
      name: addOnItemName,
      quantity: increaseQuantity(),
    });
  };

  const validateEmailVolumes = () => {
    if (!isEmailVolume) return true;
    let currCount = 0;
    const currentEmailVolumes = addOnItems?.filter((item) =>
      emailVolumeNames.includes(item.name),
    );
    if (currentEmailVolumes && currentEmailVolumes.length > 0) {
      currentEmailVolumes.forEach((item) => {
        currCount += item.quantity;
      });
    }
    const canPurchaseMore = canPurchaseMoreEmailVolume(
      addOnItemName,
      currCount + 1,
    );
    if (!canPurchaseMore) {
      setError(
        `You have reached the maximum number of ${addOns[addOnItemName]?.max_quantity} active email volume packages.`,
      );
    }
    return canPurchaseMore;
  };

  const increaseQuantity = () => {
    let currQuantity: number = quantity;
    setError("");
    return currQuantity + 1;
  };

  const renderPrice = () => {
    if (
      addOnItemName === AddOnItemName.email_volume_monthly ||
      addOnItemName === AddOnItemName.email_volume_pro_monthly
    ) {
      return "mo";
    } else if (
      addOnItemName === AddOnItemName.email_volume_yearly ||
      addOnItemName === AddOnItemName.email_volume_pro_yearly
    ) {
      return "yr";
    }
    return unit;
  };

  const hasReachedMaxQuantity = quantity >= addOns[addOnItemName]?.max_quantity;
  useEffect(() => {
    if (error && !hasReachedMaxQuantity) {
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReachedMaxQuantity]);

  if (isAtMaxEmailVolume()) {
    return <></>;
  }

  return (
    <Grid sx={styles.card}>
      <Box sx={styles.enhance}>Enhance Your Plan</Box>
      <Typography variant="h6" gutterBottom>
        Make it a Bundle: {addOns[addOnItemName]?.display_name}{" "}
        {BundleIcons[addOnItemName as keyof typeof BundleIcons]}
      </Typography>

      <Typography
        component="div"
        sx={{ color: "green.main", fontSize: 16, fontWeight: 600 }}
        gutterBottom
      >
        ${addOns[addOnItemName]?.price}.00/{renderPrice()}
      </Typography>

      <Typography>{addOns[addOnItemName]?.description}</Typography>

      <FormHelperText sx={{ fontSize: 10, color: "error.main" }}>
        <em>{error}</em>
      </FormHelperText>

      <LoadingButton
        variant="contained"
        disableElevation
        sx={{ mt: 2 }}
        loading={applyAddOnLoading === addOnItemName}
        fullWidth
        onClick={handleAddToOrder}
      >
        + Add {unit}
      </LoadingButton>

      {quantity > 0 && (
        <Typography sx={{ my: 2 }}>
          <Box
            component="i"
            className="fa-solid fa-circle-check"
            sx={{ color: "green.main", mr: 1 }}
          />{" "}
          You have added{" "}
          <strong>
            {quantity} {unit}
            {quantity > 1 ? "s" : ""}
          </strong>{" "}
          to your cart{" "}
        </Typography>
      )}
    </Grid>
  );
};

export default EmailAddOn;
